import {
  Add as AddIcon,
  Assessment as AssessmentIcon,
  Calculate as CalculateIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  GridOn as GridOnIcon,
  Groups as GroupsIcon,
  Lock as LockIcon,
  Psychology as PsychologyIcon,
  Public as PublicIcon,
  SportsSoccer as SportsSoccerIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Context } from '../../service/Context';
import theme from '../../styles/theme';
import { capitalizar, formatearFecha, ordenarLista } from '../../utils/UtilesComun';
import { obtenerColorNota } from '../../utils/UtilesJugador';

/**
 * Opciones de calificación de 10 a 0 con saltos de 0.5 (igual que en valoraciones de scouting).
 */
const OPCIONES_CALIFICACION = [];
for (let i = 10; i >= 0; i -= 0.5) {
  OPCIONES_CALIFICACION.push(Number(i.toFixed(1)));
}

/**
 * Selector de calificación con la paleta de colores de notas del sistema.
 */
const SelectorCalificacion = ({ value, onChange, placeholder = '-' }) => {
  const estilo = obtenerColorNota(value);
  const tieneValor = value !== '' && value !== null && value !== undefined;

  return (
    <Select
      size="small"
      value={value ?? ''}
      onChange={(e) => {
        const val = e.target.value;
        onChange(val === '' ? '' : parseFloat(val));
      }}
      displayEmpty
      sx={{
        height: 28,
        minWidth: 70,
        maxWidth: 78,
        fontSize: '0.82rem',
        fontWeight: tieneValor ? 700 : 500,
        backgroundColor: estilo.bg,
        color: estilo.color,
        border: `1px solid ${estilo.border}`,
        borderRadius: '5px',
        '& .MuiSelect-select': {
          py: 0.35,
          px: 1,
          color: estilo.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiSelect-icon': {
          color: estilo.color,
          fontSize: '1.1rem',
        },
        '& fieldset': {
          border: 'none',
        },
      }}
    >
      <MenuItem value="" sx={{ fontSize: '0.82rem' }}>
        <em>{placeholder}</em>
      </MenuItem>
      {OPCIONES_CALIFICACION.map((opt) => {
        const optColor = obtenerColorNota(opt);
        return (
          <MenuItem
            key={opt}
            value={opt}
            sx={{
              fontSize: '0.82rem',
              fontWeight: 600,
              backgroundColor: `${optColor.bg}88`,
              color: optColor.color,
              '&:hover': {
                backgroundColor: optColor.bg,
              },
            }}
          >
            {opt.toFixed(1)}
          </MenuItem>
        );
      })}
    </Select>
  );
};

SelectorCalificacion.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

/**
 * Fila de formulario para cada una de las 4 áreas del informe.
 */
const CampoAreaInforme = ({ icon, titulo, nota, obs, onNotaChange, onObsChange, placeholder }) => (
  <Box
    sx={{
      p: 1.5,
      mb: 1.25,
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      border: '1px solid #e2e8f0',
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        borderColor: '#cbd5e1',
        boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: theme.palette.primary.main, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: '#1e293b' }}>{titulo}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 500 }}>Nota:</Typography>
        <SelectorCalificacion value={nota} onChange={onNotaChange} />
      </Box>
    </Box>
    <TextField
      fullWidth
      size="small"
      multiline
      minRows={2}
      maxRows={5}
      placeholder={placeholder}
      value={obs ?? ''}
      onChange={(e) => onObsChange(e.target.value)}
      sx={{
        backgroundColor: '#f8fafc',
        borderRadius: '6px',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.82rem',
          borderRadius: '6px',
          '& fieldset': { borderColor: '#e2e8f0' },
          '&:hover fieldset': { borderColor: '#cbd5e1' },
          '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
        },
      }}
    />
  </Box>
);

CampoAreaInforme.propTypes = {
  icon: PropTypes.node.isRequired,
  titulo: PropTypes.string.isRequired,
  nota: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  obs: PropTypes.string,
  onNotaChange: PropTypes.func.isRequired,
  onObsChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

/**
 * Chip para mostrar una nota de área en la ficha del informe.
 */
const BadgeAreaResumen = ({ titulo, nota, obs }) => {
  const tieneNota = nota !== null && nota !== undefined && nota !== '';
  const estilo = obtenerColorNota(nota);

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: '6px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase' }}>
          {titulo}
        </Typography>
        <Chip
          size="small"
          label={tieneNota ? Number(nota).toFixed(1) : '-'}
          sx={{
            height: 22,
            minWidth: 42,
            fontSize: '0.78rem',
            fontWeight: 700,
            backgroundColor: estilo.bg,
            color: estilo.color,
            border: `1px solid ${estilo.border}`,
            borderRadius: '4px',
            '& .MuiChip-label': { px: 0.5 },
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: '0.78rem',
          color: obs ? '#334155' : '#94a3b8',
          fontStyle: obs ? 'normal' : 'italic',
          lineHeight: 1.4,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          flex: 1,
        }}
      >
        {obs || 'Sin observaciones'}
      </Typography>
    </Box>
  );
};

BadgeAreaResumen.propTypes = {
  titulo: PropTypes.string.isRequired,
  nota: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  obs: PropTypes.string,
};

/**
 * Modal principal para gestionar los informes detallados de un jugador.
 */
const ModalInformesJugador = ({
  open,
  onClose,
  idJugador,
  nombreJugador,
  informes = [],
  onUpdate,
  initialCreateMode = false,
}) => {
  const { idUsuario, anadirInforme, editarInforme, eliminarInforme } = useContext(Context);

  const [formOpen, setFormOpen] = useState(false);
  const [editInformeId, setEditInformeId] = useState(null);

  // Estados del formulario
  const [notaTactico, setNotaTactico] = useState('');
  const [obsTactico, setObsTactico] = useState('');
  const [notaTecnico, setNotaTecnico] = useState('');
  const [obsTecnico, setObsTecnico] = useState('');
  const [notaPsicologico, setNotaPsicologico] = useState('');
  const [obsPsicologico, setObsPsicologico] = useState('');
  const [notaEntorno, setNotaEntorno] = useState('');
  const [obsEntorno, setObsEntorno] = useState('');
  const [notaFinal, setNotaFinal] = useState('');
  const [comentarioFinal, setComentarioFinal] = useState('');
  const [publica, setPublica] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Menú de visibilidad en línea
  const [visibilidadAnchorEl, setVisibilidadAnchorEl] = useState(null);
  const [visibilidadTargetInforme, setVisibilidadTargetInforme] = useState(null);

  // Cálculo automático de la media aritmética de las 4 áreas
  const mediaCalculada = useMemo(() => {
    const notas = [notaTactico, notaTecnico, notaPsicologico, notaEntorno]
      .filter((n) => n !== '' && n !== null && n !== undefined)
      .map(Number);
    if (notas.length === 0) {
      return null;
    }
    const suma = notas.reduce((acc, curr) => acc + curr, 0);
    return Number((suma / notas.length).toFixed(1));
  }, [notaTactico, notaTecnico, notaPsicologico, notaEntorno]);

  const handleOpenCreateForm = () => {
    setEditInformeId(null);
    setNotaTactico('');
    setObsTactico('');
    setNotaTecnico('');
    setObsTecnico('');
    setNotaPsicologico('');
    setObsPsicologico('');
    setNotaEntorno('');
    setObsEntorno('');
    setNotaFinal('');
    setComentarioFinal('');
    setPublica(false);
    setFormOpen(true);
  };

  useEffect(() => {
    if (open && initialCreateMode) {
      handleOpenCreateForm();
    }
  }, [open, initialCreateMode]);

  const handleOpenEditForm = (inf) => {
    setEditInformeId(inf.idInforme);
    setNotaTactico(inf.notaTactico ?? '');
    setObsTactico(inf.obsTactico || '');
    setNotaTecnico(inf.notaTecnico ?? '');
    setObsTecnico(inf.obsTecnico || '');
    setNotaPsicologico(inf.notaPsicologico ?? '');
    setObsPsicologico(inf.obsPsicologico || '');
    setNotaEntorno(inf.notaEntorno ?? '');
    setObsEntorno(inf.obsEntorno || '');
    setNotaFinal(inf.notaFinal ?? '');
    setComentarioFinal(inf.comentarioFinal || '');
    setPublica(inf.publica || false);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditInformeId(null);
  };

  const handleCloseModal = () => {
    handleCloseForm();
    onClose();
  };

  const handleAplicarMedia = () => {
    if (mediaCalculada !== null) {
      setNotaFinal(mediaCalculada);
    }
  };

  const handleSaveForm = async () => {
    setGuardando(true);
    try {
      const payload = {
        idUsuario,
        notaTactico: notaTactico === '' ? null : Number(notaTactico),
        obsTactico: obsTactico.trim() || null,
        notaTecnico: notaTecnico === '' ? null : Number(notaTecnico),
        obsTecnico: obsTecnico.trim() || null,
        notaPsicologico: notaPsicologico === '' ? null : Number(notaPsicologico),
        obsPsicologico: obsPsicologico.trim() || null,
        notaEntorno: notaEntorno === '' ? null : Number(notaEntorno),
        obsEntorno: obsEntorno.trim() || null,
        notaFinal: notaFinal === '' ? null : Number(notaFinal),
        comentarioFinal: comentarioFinal.trim() || null,
        publica,
      };

      let updated;
      if (editInformeId) {
        updated = await editarInforme(editInformeId, {
          ...payload,
          idInforme: editInformeId,
        });
      } else {
        updated = await anadirInforme(idJugador, payload);
      }

      handleCloseForm();
      if (onUpdate && updated) {
        onUpdate(updated);
      }
    } catch (err) {
      console.error('Error al guardar informe:', err);
    } finally {
      setGuardando(false);
    }
  };

  const handleDelete = async (idInforme) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este informe? Esta acción no se puede deshacer.')) {
      try {
        const updated = await eliminarInforme(idInforme);
        if (onUpdate && updated) {
          onUpdate(updated);
        }
      } catch (err) {
        console.error('Error al eliminar informe:', err);
      }
    }
  };

  const handleOpenVisibilityMenu = (event, inf) => {
    event.stopPropagation();
    setVisibilidadAnchorEl(event.currentTarget);
    setVisibilidadTargetInforme(inf);
  };

  const handleCloseVisibilityMenu = () => {
    setVisibilidadAnchorEl(null);
    setVisibilidadTargetInforme(null);
  };

  const handleChangeVisibility = async (nuevaPublica) => {
    if (!visibilidadTargetInforme) {
      return;
    }
    const inf = visibilidadTargetInforme;
    handleCloseVisibilityMenu();
    try {
      const updated = await editarInforme(inf.idInforme, {
        ...inf,
        publica: nuevaPublica,
      });
      if (onUpdate && updated) {
        onUpdate(updated);
      }
    } catch (err) {
      console.error('Error al cambiar visibilidad del informe:', err);
    }
  };

  const informesOrdenados = useMemo(() => {
    return ordenarLista(informes || [], { field: 'timestamp', direction: 'desc' });
  }, [informes]);

  const totalInformes = informesOrdenados.length;

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        },
      }}
    >
      {/* Cabecera del Modal */}
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 2.25,
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '10px',
              backgroundColor: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#065f46',
            }}
          >
            <AssessmentIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#0f172a', lineHeight: 1.2 }}>
                Informes de Scouting
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.15,
                  borderRadius: '12px',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  backgroundColor: totalInformes > 0 ? '#dcfce7' : '#f1f5f9',
                  color: totalInformes > 0 ? '#15803d' : '#64748b',
                  border: `1px solid ${totalInformes > 0 ? '#86efac' : '#cbd5e1'}`,
                }}
              >
                {totalInformes} {totalInformes === 1 ? 'informe' : 'informes'}
              </Box>
            </Box>
            {nombreJugador && (
              <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.25 }}>
                {capitalizar(nombreJugador)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!formOpen && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenCreateForm}
              sx={{
                borderRadius: '20px',
                px: 2,
                py: 0.6,
                backgroundColor: '#0f766e',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                boxShadow: '0 2px 4px rgba(15, 118, 110, 0.2)',
                '&:hover': {
                  backgroundColor: '#0d9488',
                  boxShadow: '0 4px 6px rgba(15, 118, 110, 0.3)',
                },
              }}
            >
              Nuevo informe
            </Button>
          )}
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              color: '#94a3b8',
              '&:hover': {
                color: '#475569',
                backgroundColor: '#f1f5f9',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Contenido del Modal */}
      <DialogContent sx={{ p: 3, maxHeight: '74vh', overflowY: 'auto' }}>
        {/* Formulario integrado de creación / edición */}
        {formOpen && (
          <Box
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: '12px',
              backgroundColor: '#f8fafc',
              border: '1.5px solid #0f766e',
              boxShadow: '0 4px 14px rgba(15, 118, 110, 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.98rem', color: '#0f172a' }}>
                {editInformeId ? 'Editar informe de jugador' : 'Nuevo informe detallado de jugador'}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                4 áreas de evaluación + Conclusión final
              </Typography>
            </Box>

            {/* 1. Táctico */}
            <CampoAreaInforme
              icon={<GridOnIcon sx={{ fontSize: 18 }} />}
              titulo="1. Área Táctica"
              nota={notaTactico}
              obs={obsTactico}
              onNotaChange={setNotaTactico}
              onObsChange={setObsTactico}
              placeholder="Posicionamiento táctico, lectura de juego, apoyos, transiciones defensivas/ofensivas, toma de decisiones..."
            />

            {/* 2. Técnico */}
            <CampoAreaInforme
              icon={<SportsSoccerIcon sx={{ fontSize: 18 }} />}
              titulo="2. Área Técnica"
              nota={notaTecnico}
              obs={obsTecnico}
              onNotaChange={setNotaTecnico}
              onObsChange={setObsTecnico}
              placeholder="Control de balón, calidad de pase, golpeo, regate, pierna no hábil, recursos en espacio reducido..."
            />

            {/* 3. Psicológico */}
            <CampoAreaInforme
              icon={<PsychologyIcon sx={{ fontSize: 18 }} />}
              titulo="3. Área Psicológica"
              nota={notaPsicologico}
              obs={obsPsicologico}
              onNotaChange={setNotaPsicologico}
              onObsChange={setObsPsicologico}
              placeholder="Concentración, actitud competitiva, liderazgo, comunicación en el campo, gestión de errores..."
            />

            {/* 4. Entorno */}
            <CampoAreaInforme
              icon={<GroupsIcon sx={{ fontSize: 18 }} />}
              titulo="4. Entorno"
              nota={notaEntorno}
              obs={obsEntorno}
              onNotaChange={setNotaEntorno}
              onObsChange={setObsEntorno}
              placeholder="Compromiso familiar, entorno socio-deportivo, puntualidad, hábitos, conducta fuera del campo..."
            />

            {/* Sección de Nota Final y Conclusión */}
            <Box
              sx={{
                p: 2,
                mt: 2,
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                border: '1.5px solid #cbd5e1',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>
                    Evaluación Global y Nota Final
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                    El usuario define la nota final (o puede aplicar la media de las áreas).
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {mediaCalculada !== null && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CalculateIcon sx={{ fontSize: 16 }} />}
                      onClick={handleAplicarMedia}
                      sx={{
                        height: 28,
                        fontSize: '0.74rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#0f766e',
                        borderColor: '#99f6e4',
                        backgroundColor: '#f0fdfa',
                        '&:hover': {
                          borderColor: '#0f766e',
                          backgroundColor: '#ccfbf1',
                        },
                      }}
                    >
                      Media: {mediaCalculada.toFixed(1)}
                    </Button>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.80rem', color: '#1e293b' }}>Nota Final:</Typography>
                    <SelectorCalificacion value={notaFinal} onChange={setNotaFinal} />
                  </Box>
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                placeholder="Conclusión final del scout, potencial del jugador, fortalezas clave y recomendaciones de seguimiento o fichaje..."
                value={comentarioFinal ?? ''}
                onChange={(e) => setComentarioFinal(e.target.value)}
                sx={{
                  backgroundColor: '#f8fafc',
                  borderRadius: '6px',
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.84rem',
                    borderRadius: '6px',
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                  },
                }}
              />
            </Box>

            {/* Controles de Visibilidad y Guardado */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
                mt: 2.5,
              }}
            >
              {/* Visibilidad */}
              <Tooltip
                title={
                  publica
                    ? 'Pública: visible para todos los scouts autorizados del club'
                    : 'Privada: visible únicamente para ti'
                }
                arrow
              >
                <Box
                  onClick={() => setPublica(!publica)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.6,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundColor: publica ? '#ecfdf5' : '#f1f5f9',
                    border: `1px solid ${publica ? '#a7f3d0' : '#cbd5e1'}`,
                    color: publica ? '#065f46' : '#475569',
                    fontSize: '0.80rem',
                    fontWeight: 600,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: publica ? '#d1fae5' : '#e2e8f0',
                    },
                  }}
                >
                  {publica ? <PublicIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                  <span>{publica ? 'Pública (Club)' : 'Privada (Solo tú)'}</span>
                </Box>
              </Tooltip>

              {/* Botones de acción */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCloseForm}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#64748b',
                    borderColor: '#cbd5e1',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                      borderColor: '#94a3b8',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveForm}
                  disabled={guardando}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#0f766e',
                    '&:hover': { backgroundColor: '#0d9488' },
                  }}
                >
                  {guardando ? 'Guardando...' : editInformeId ? 'Actualizar informe' : 'Guardar informe'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Listado de Informes */}
        {informesOrdenados && informesOrdenados.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {informesOrdenados.map((inf) => {
              const esPropietario = inf.idUsuario === idUsuario;
              const tieneNotaFinal = inf.notaFinal !== null && inf.notaFinal !== undefined && inf.notaFinal !== '';
              const estiloNotaFinal = obtenerColorNota(inf.notaFinal);

              return (
                <Box
                  key={inf.idInforme}
                  sx={{
                    p: 2.25,
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      borderColor: '#cbd5e1',
                    },
                  }}
                >
                  {/* Cabecera del informe individual */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 1.75,
                      pb: 1.25,
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>
                          {inf.nombreUsuario || `Usuario #${inf.idUsuario}`}
                        </Typography>
                        <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                          {formatearFecha(inf.timestamp)}
                        </Typography>
                      </Box>

                      {/* Insignia de Nota Final */}
                      <Chip
                        size="small"
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>Nota Final:</span>
                            <span>{tieneNotaFinal ? Number(inf.notaFinal).toFixed(1) : '-'}</span>
                          </Box>
                        }
                        sx={{
                          height: 26,
                          fontWeight: 700,
                          fontSize: '0.82rem',
                          backgroundColor: estiloNotaFinal.bg,
                          color: estiloNotaFinal.color,
                          border: `1px solid ${estiloNotaFinal.border}`,
                          borderRadius: '6px',
                        }}
                      />
                    </Box>

                    {/* Visibilidad y Acciones */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {esPropietario ? (
                        <Tooltip title="Clic para cambiar visibilidad" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenVisibilityMenu(e, inf)}
                            sx={{
                              color: inf.publica ? '#059669' : '#64748b',
                              backgroundColor: inf.publica ? '#ecfdf5' : '#f1f5f9',
                              border: `1px solid ${inf.publica ? '#a7f3d0' : '#e2e8f0'}`,
                              p: 0.6,
                              borderRadius: '6px',
                              '&:hover': {
                                backgroundColor: inf.publica ? '#d1fae5' : '#e2e8f0',
                              },
                            }}
                          >
                            {inf.publica ? <PublicIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={inf.publica ? 'Pública' : 'Privada'} arrow>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              p: 0.6,
                              borderRadius: '6px',
                              color: inf.publica ? '#059669' : '#64748b',
                              backgroundColor: inf.publica ? '#ecfdf5' : '#f1f5f9',
                              border: `1px solid ${inf.publica ? '#a7f3d0' : '#e2e8f0'}`,
                            }}
                          >
                            {inf.publica ? <PublicIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                          </Box>
                        </Tooltip>
                      )}

                      {esPropietario && (
                        <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                          <Tooltip title="Editar informe" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditForm(inf)}
                              sx={{
                                color: '#0f766e',
                                '&:hover': { backgroundColor: '#f0fdfa' },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar informe" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(inf.idInforme)}
                              sx={{
                                color: '#ef4444',
                                '&:hover': { backgroundColor: '#fef2f2' },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Conclusión o Comentario Final destacado */}
                  {inf.comentarioFinal && (
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1.75,
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        borderLeft: `4px solid ${estiloNotaFinal.border}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', mb: 0.25 }}>
                        CONCLUSIÓN / COMENTARIO GLOBAL:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.84rem',
                          color: '#1e293b',
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {inf.comentarioFinal}
                      </Typography>
                    </Box>
                  )}

                  {/* Desglose de las 4 Áreas */}
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={6}>
                      <BadgeAreaResumen titulo="Táctico" nota={inf.notaTactico} obs={inf.obsTactico} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <BadgeAreaResumen titulo="Técnico" nota={inf.notaTecnico} obs={inf.obsTecnico} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <BadgeAreaResumen titulo="Psicológico" nota={inf.notaPsicologico} obs={inf.obsPsicologico} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <BadgeAreaResumen titulo="Entorno" nota={inf.notaEntorno} obs={inf.obsEntorno} />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>
        ) : (
          !formOpen && (
            <Box
              sx={{
                py: 6,
                px: 3,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
                border: '1.5px dashed #cbd5e1',
                backgroundColor: '#f8fafc',
              }}
            >
              <AssessmentIcon sx={{ fontSize: 44, color: '#94a3b8', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#334155', mb: 0.5 }}>
                Sin informes registrados
              </Typography>
              <Typography sx={{ fontSize: '0.84rem', color: '#64748b', maxWidth: 420, mb: 2.5 }}>
                Este jugador aún no tiene informes multidimensionales. Puedes realizar una evaluación completa en sus
                áreas táctica, técnica, psicológica y de entorno.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateForm}
                sx={{
                  borderRadius: '20px',
                  px: 2.5,
                  py: 0.75,
                  backgroundColor: '#0f766e',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  '&:hover': { backgroundColor: '#0d9488' },
                }}
              >
                Crear primer informe
              </Button>
            </Box>
          )
        )}

        {/* Menú de visibilidad contextual */}
        <Menu
          anchorEl={visibilidadAnchorEl}
          open={Boolean(visibilidadAnchorEl)}
          onClose={handleCloseVisibilityMenu}
          PaperProps={{
            sx: {
              borderRadius: '10px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e2e8f0',
            },
          }}
        >
          <MenuItem
            onClick={() => handleChangeVisibility(false)}
            sx={{ fontSize: '0.82rem', gap: 1, py: 1 }}
            selected={visibilidadTargetInforme && !visibilidadTargetInforme.publica}
          >
            <LockIcon sx={{ fontSize: 16, color: '#64748b' }} />
            <span>Privada (Solo tú)</span>
          </MenuItem>
          <MenuItem
            onClick={() => handleChangeVisibility(true)}
            sx={{ fontSize: '0.82rem', gap: 1, py: 1 }}
            selected={visibilidadTargetInforme && visibilidadTargetInforme.publica}
          >
            <PublicIcon sx={{ fontSize: 16, color: '#059669' }} />
            <span>Pública (Club)</span>
          </MenuItem>
        </Menu>
      </DialogContent>
    </Dialog>
  );
};

ModalInformesJugador.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  idJugador: PropTypes.number.isRequired,
  nombreJugador: PropTypes.string,
  informes: PropTypes.array,
  onUpdate: PropTypes.func,
  initialCreateMode: PropTypes.bool,
};

export default ModalInformesJugador;
